/**
 * A tiny pub/sub so `realClient.ts` (plain fetch, outside React) can tell
 * `AuthContext` "the session is no longer valid server-side" without a circular
 * import: `AuthProvider` itself calls `useApiClient()`, so the client can't
 * depend back on the auth context. `AuthProvider` subscribes once; any API call
 * that gets a 401 from an authenticated endpoint fires this, `AuthProvider`
 * clears its session state, and `ProtectedRoute` reactively sends the operator
 * back to `/login` — no imperative navigation needed here.
 */
type Listener = () => void;

const listeners = new Set<Listener>();

export function onUnauthorized(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Exported for realClient.ts only; call on any 401 from an authenticated call
 * (never from /api/auth/login itself — there a 401 just means "wrong credentials"). */
export function emitUnauthorized(): void {
  for (const listener of listeners) {
    listener();
  }
}
